import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { FormColumn, FormColumns, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { EditUserInformationDialog } from '@/features/user-management/EditUserInformationDialog'
import { type EditUserInformationDialogViewModel } from './EditUserInformationDialogViewModel'

export interface EditUserInformationDialogContainerProps {
  vm: EditUserInformationDialogViewModel
}

export const EditUserInformationDialogContainer = observer(
  function EditUserInformationDialogContainer({
    vm,
  }: EditUserInformationDialogContainerProps) {
    const itemSource = useMemo(
      () =>
        ItemSource.static(vm.userTypeOptions, (item, text) => {
          return item.title.toLowerCase().includes(text.toLowerCase())
        }),
      [vm.userTypeOptions],
    )
    const selectProps = useCustomSelectStateFromItemSource({ itemSource })

    return (
      <EditUserInformationDialog
        showing={vm.modal.showing}
        onClose={vm.modal.close}
        onSubmit={vm.submit}
        isSubmitPending={vm.submit.pending}
      >
        <FormRow>
          <FormColumns>
            <FormColumn>
              <TextInputContainer
                autoFocus
                label="First Name"
                field="givenName"
                model={vm}
                error={vm.validation.getError('givenName')}
                data-test="given-name-input"
              />
            </FormColumn>

            <FormColumn>
              <TextInputContainer
                autoFocus
                label="Last Name"
                field="familyName"
                model={vm}
                error={vm.validation.getError('familyName')}
                data-test="family-name-input"
              />
            </FormColumn>
          </FormColumns>
        </FormRow>

        <FormRow>
          <TextInputContainer
            autoFocus
            label="Email"
            field="email"
            model={vm}
            error={vm.validation.getError('email')}
            data-test="email-input"
          />
        </FormRow>

        <FormRow>
          <FormColumns>
            <FormColumn>
              <TextInputContainer
                autoFocus
                label="Phone number"
                field="phone"
                data-test="phone-input"
                model={vm}
                error={vm.validation.getError('phone')}
              />
            </FormColumn>

            {vm.isPlatformAdmin && (
              <FormColumn>
                <FormGroupContainer label="User type">
                  <CustomSelect
                    {...selectProps}
                    multi={false}
                    value={vm.selectedUserType}
                    itemToString={(i) => i.title}
                    icon="dropdown"
                    iconColor="var(--grey600)"
                    testId="edit-user-type-select"
                    onChange={vm.setUserType}
                  />
                </FormGroupContainer>
              </FormColumn>
            )}
          </FormColumns>
        </FormRow>
      </EditUserInformationDialog>
    )
  },
)
